// ===========================================
// Custom SMS & WhatsApp API
// ===========================================
// API client for custom SMS/WhatsApp campaigns feature

import { apiClient } from '../services/api';
import {
  CustomSmsCampaign,
  CustomSmsCampaignListItem,
  CreateCustomSmsCampaignData,
  UpdateCustomSmsCampaignData,
  ScheduleCustomSmsCampaignData,
  TestSmsData,
  CustomSmsCampaignFilters,
  PaginatedSmsCampaignResponse,
  SmsRecipientListResult,
  SmsCampaignStats,
  SmsCampaignAnalyticsRecord,
} from '../types/customSms.types';

const BASE_URL = '/admin/custom-sms';

// ===========================================
// API RESPONSE WRAPPER
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  success: boolean;
  campaign?: T;
  message?: string;
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ===========================================
// CRUD OPERATIONS
// ===========================================

export async function createCustomSmsCampaign(
  data: CreateCustomSmsCampaignData
): Promise<CustomSmsCampaign> {
  const response = await apiClient.post<ApiResponse<CustomSmsCampaign>>(BASE_URL, data);
  return (response as any).campaign || response.data;
}

export async function getCustomSmsCampaigns(
  filters?: CustomSmsCampaignFilters
): Promise<PaginatedSmsCampaignResponse> {
  const response = await apiClient.get<{
    success: boolean;
    data: CustomSmsCampaignListItem[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>(BASE_URL, { params: filters });
  return {
    data: (response as any).data || [],
    total: (response as any).total || 0,
    page: (response as any).page || 1,
    limit: (response as any).limit || 20,
    totalPages: (response as any).totalPages || 1,
  };
}

export async function getCustomSmsCampaign(campaignId: string): Promise<CustomSmsCampaign> {
  const response = await apiClient.get<{ success: boolean; campaign: CustomSmsCampaign }>(
    `${BASE_URL}/${campaignId}`
  );
  return (response as any).campaign;
}

export async function updateCustomSmsCampaign(
  campaignId: string,
  data: UpdateCustomSmsCampaignData
): Promise<CustomSmsCampaign> {
  const response = await apiClient.patch<{ success: boolean; campaign: CustomSmsCampaign }>(
    `${BASE_URL}/${campaignId}`,
    data
  );
  return (response as any).campaign;
}

export async function deleteCustomSmsCampaign(campaignId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${campaignId}`);
}

export async function duplicateCustomSmsCampaign(
  campaignId: string
): Promise<CustomSmsCampaign> {
  const response = await apiClient.post<{ success: boolean; campaign: CustomSmsCampaign }>(
    `${BASE_URL}/${campaignId}/duplicate`
  );
  return (response as any).campaign;
}

// ===========================================
// SCHEDULING
// ===========================================

export async function scheduleCustomSmsCampaign(
  campaignId: string,
  data: ScheduleCustomSmsCampaignData
): Promise<CustomSmsCampaign> {
  const response = await apiClient.post<{ success: boolean; campaign: CustomSmsCampaign }>(
    `${BASE_URL}/${campaignId}/schedule`,
    data
  );
  return (response as any).campaign;
}

export async function cancelScheduledSmsCampaign(
  campaignId: string
): Promise<CustomSmsCampaign> {
  const response = await apiClient.post<{ success: boolean; campaign: CustomSmsCampaign }>(
    `${BASE_URL}/${campaignId}/cancel`
  );
  return (response as any).campaign;
}

// ===========================================
// SENDING
// ===========================================

export async function sendCustomSmsCampaign(
  campaignId: string
): Promise<{ message: string }> {
  const response = await apiClient.post<{ success: boolean; message: string }>(
    `${BASE_URL}/${campaignId}/send`
  );
  return { message: (response as any).message || 'Campaign is being distributed' };
}

export async function sendTestSms(
  campaignId: string,
  data: TestSmsData
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post<{ success: boolean; message: string }>(
    `${BASE_URL}/${campaignId}/test`,
    data
  );
  return response as any;
}

// ===========================================
// RECIPIENTS
// ===========================================

export async function getSmsCampaignRecipients(
  campaignId: string
): Promise<SmsRecipientListResult> {
  const response = await apiClient.get<{ success: boolean } & SmsRecipientListResult>(
    `${BASE_URL}/${campaignId}/recipients`
  );
  return {
    recipients: (response as any).recipients || [],
    total: (response as any).total || 0,
    breakdown: (response as any).breakdown || { byType: { parents: 0, teachers: 0 }, bySmsEligible: 0, byWhatsAppEligible: 0 },
  };
}

// ===========================================
// ANALYTICS
// ===========================================

export async function getSmsCampaignStats(
  campaignId: string
): Promise<SmsCampaignStats> {
  const response = await apiClient.get<{ success: boolean; stats: SmsCampaignStats }>(
    `${BASE_URL}/${campaignId}/analytics`
  );
  return (response as any).stats;
}

export async function getSmsCampaignAnalyticsRecords(
  campaignId: string,
  params?: { page?: number; limit?: number }
): Promise<{ records: SmsCampaignAnalyticsRecord[]; total: number }> {
  // Backend spreads PaginatedResult: { success, data, total, page, limit, totalPages }
  const response = await apiClient.get<{
    success: boolean;
    data: SmsCampaignAnalyticsRecord[];
    total: number;
  }>(`${BASE_URL}/${campaignId}/analytics/records`, { params });
  return {
    records: (response as any).data || [],
    total: (response as any).total || 0,
  };
}
